import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as fromAuthActions from './auth.actions';
import { of } from 'rxjs';
import { AuthService } from './auth.service';
import { SubscriberService } from '@emi/shared/data-access';
import { Constants } from './constants';
import { Subscription } from '@emi/shared/models';
import { LocalStorageValues } from '@emi/shared/constants';

@Injectable({ providedIn: 'root' })
export class AuthEffects {

  constructor(private actions$: Actions, private authService: AuthService, private subscriberService: SubscriberService) { }

  private login$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuthActions.login),
        switchMap(() => of(this.authService.login()))
      ), { dispatch: false }
  );

  private checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.checkAuth),
      switchMap(() =>
        this.authService.checkAuth()
          .pipe(
            map((loginResponse) =>
              fromAuthActions.checkAuthComplete({
                loginResponse
              })
            )
          )
      )
    )
  );

  accessTokenComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.updateAccessToken),
      switchMap(() => of(fromAuthActions.updateAccessTokenComplete({ accessToken: this.authService.accessToken() })))
    )
  );

  private checkAuthComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.checkAuthComplete),
      switchMap(({ loginResponse }) => {
        if (loginResponse.isAuthenticated) {
          return this.authService.userData().pipe(
            switchMap(userData => this.subscriberService.getSubscriptions(userData.userData.sub, loginResponse.accessToken).pipe(
              catchError(_ => of([] as Subscription[])),
              switchMap(subscriptions => {
                var subscriberId = localStorage.getItem(LocalStorageValues.LOCAL_STORAGE_SID_KEY) ?? ''

                if (subscriptions.length == 0 && subscriberId){
                  // internal user
                    return this.subscriberService.getSubscriptionById(loginResponse.accessToken, subscriberId).pipe(
                    switchMap(sub => {

                      subscriptions = [sub];

                      return this.subscriberService.getSubscriberJwt(subscriberId, loginResponse.accessToken)
                        .pipe(
                          catchError(_ => of(_.error.text as string)),
                          map((subscriberJwt) => {
                            return fromAuthActions.loginComplete({
                              subscription: subscriptions.find(x => x.applicationId == Constants.APPLICATION_ID)!,
                              userData,
                              loginResponse,
                              subscriberJwt,
                              subscriptions: subscriptions
                            })
                          })
                        )
                    })
                  )
                } else {
                  return this.subscriberService.getSubscriberJwt(String(subscriptions.find(x => x.applicationId == Constants.APPLICATION_ID)!.id), loginResponse.accessToken)
                    .pipe(
                      catchError(_ => of(_.error.text as string)),
                      map((subscriberJwt) => {

                        return fromAuthActions.loginComplete({
                          subscription: subscriptions.find(x => x.applicationId == Constants.APPLICATION_ID)!,
                          userData,
                          loginResponse,
                          subscriberJwt,
                          subscriptions: subscriptions
                        })

                      })
                    )
                }
              })))
          );
        } else {
          return of(fromAuthActions.login());
        }
      })
    )
  );

  private logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.logout),
      tap(() => this.authService.signOut()),
      map(() => fromAuthActions.logoutComplete())
    )
  );

  private logoutComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuthActions.logoutComplete),
    ), { dispatch: false }
  );
}
